/**
 * Qué sesión proponer hoy (tarea 14; decisión de Janeth, 23 oct 2026).
 *
 * - "Hoy toca": la rutina asignada a este día de la semana.
 * - "Te faltó": la sesión más reciente de los últimos 6 días que no se hizo
 *   desde ese día. Se ofrece retomarla y se puede elegir otra.
 * - "Sigue": en las sesiones sin días fijos (las A, B y C de Fernando), la que
 *   sigue a la última terminada.
 * - Nunca se avanza ni se completa una sesión sola: esto solo propone.
 */

import type { Modalidad, Routine, Workout } from '@/db/types';
import { diaSemana, sumarDias, type FechaLocal } from './dates';
import { fechaLocalDe } from './time';
import { modalidadDe } from './modalidad';

export interface SesionPropuesta {
  routineId: string;
  nombre: string;
  /** El día al que corresponde. */
  fecha: FechaLocal;
  /** Ya hay un entrenamiento terminado con esta rutina desde ese día. */
  hecha: boolean;
}

export interface Propuestas {
  hoy: SesionPropuesta | null;
  pendiente: SesionPropuesta | null;
}

const DIAS_ATRAS = 6;

export function proponerSesiones(
  rutinas: readonly Pick<Routine, 'id' | 'nombre' | 'orden' | 'dias' | 'creadoEn'>[],
  terminados: readonly Pick<Workout, 'routineId' | 'fecha' | 'estado'>[],
  hoy: FechaLocal,
): Propuestas {
  const ordenadas = [...rutinas].sort((a, b) => a.orden - b.orden);
  const hechaDesde = (routineId: string, fecha: FechaLocal) =>
    terminados.some((w) => w.estado === 'terminado' && w.routineId === routineId && w.fecha >= fecha);

  const deHoy = ordenadas.find((r) => (r.dias ?? []).includes(diaSemana(hoy)));
  const propuestaHoy: SesionPropuesta | null =
    deHoy === undefined ? null : { routineId: deHoy.id, nombre: deHoy.nombre, fecha: hoy, hecha: hechaDesde(deHoy.id, hoy) };

  // Solo la sesión más reciente que tocaba: si esa se hizo (aunque fuera otro
  // día), no se buscan faltas más viejas.
  let pendiente: SesionPropuesta | null = null;
  for (let atras = 1; atras <= DIAS_ATRAS; atras++) {
    const fecha = sumarDias(hoy, -atras);
    // Una rutina creada después de ese día no se pudo haber hecho
    const tocaban = ordenadas.filter(
      (r) => (r.dias ?? []).includes(diaSemana(fecha)) && fechaLocalDe(r.creadoEn) <= fecha && r.id !== propuestaHoy?.routineId,
    );
    if (tocaban.length === 0) continue;
    const faltante = tocaban.find((r) => !hechaDesde(r.id, fecha));
    if (faltante !== undefined) pendiente = { routineId: faltante.id, nombre: faltante.nombre, fecha, hecha: false };
    break;
  }
  return { hoy: propuestaHoy, pendiente };
}

// ---------------------------------------------------------------------------
// Sesiones sin días fijos: A → B → C (Req. 10.7; Fernando, 26 oct 2026)
// ---------------------------------------------------------------------------

/**
 * Lo que propone Gym en una modalidad, y Hoy lo mismo (0.3.7): la de hoy, la
 * que faltó y la que sigue de cada ciclo. Solo cuenta un entrenamiento
 * terminado con al menos una serie: uno vacío no da nada por hecho.
 */
export function propuestasDeGym(
  rutinas: readonly (Pick<Routine, 'id' | 'nombre' | 'orden' | 'dias' | 'creadoEn' | 'plantilla'> & { modalidad?: Modalidad })[],
  terminados: readonly Pick<Workout, 'id' | 'routineId' | 'estado' | 'inicio' | 'fecha'>[],
  conSeries: ReadonlySet<string>,
  ciclos: readonly (readonly string[])[],
  modalidad: Modalidad,
  hoy: FechaLocal,
): { propuestas: Propuestas; siguientes: SiguienteDelCiclo[] } {
  const lista = rutinas.filter((r) => modalidadDe(r) === modalidad);
  const conAlgo = terminados.filter((w) => conSeries.has(w.id));
  return {
    propuestas: proponerSesiones(lista, conAlgo, hoy),
    siguientes: ciclos.flatMap((ciclo) => {
      const s = siguienteDelCiclo(ciclo, lista, conAlgo, conSeries);
      return s === null ? [] : [s];
    }),
  };
}

export interface SiguienteDelCiclo {
  routineId: string;
  nombre: string;
  /** La última sesión del ciclo terminada con al menos una serie, si hay. */
  ultima: { nombre: string; fecha: FechaLocal } | null;
}

/**
 * La sesión que sigue a la última terminada del ciclo; sin ninguna, la
 * primera. Solo cuenta un entrenamiento terminado con al menos una serie:
 * nada avanza por la fecha ni se da por hecho solo, y se puede elegir otra.
 * Las sesiones se reconocen por su clave de plantilla, así que cambiarles el
 * nombre no rompe el orden; si se borró una, se salta.
 */
export function siguienteDelCiclo(
  ciclo: readonly string[],
  rutinas: readonly Pick<Routine, 'id' | 'nombre' | 'plantilla'>[],
  entrenamientos: readonly Pick<Workout, 'id' | 'routineId' | 'estado' | 'inicio' | 'fecha'>[],
  conSeries: ReadonlySet<string>,
): SiguienteDelCiclo | null {
  const sesiones = ciclo.flatMap((clave) => {
    const r = rutinas.find((x) => x.plantilla === clave);
    return r === undefined ? [] : [r];
  });
  const primera = sesiones[0];
  if (primera === undefined) return null;
  const ids = new Set(sesiones.map((s) => s.id));
  const ultimo = entrenamientos
    .filter((w) => w.estado === 'terminado' && w.routineId !== null && ids.has(w.routineId) && conSeries.has(w.id))
    .sort((a, b) => b.inicio.localeCompare(a.inicio))[0];
  if (ultimo === undefined) return { routineId: primera.id, nombre: primera.nombre, ultima: null };
  const i = sesiones.findIndex((s) => s.id === ultimo.routineId);
  const hecha = sesiones[i];
  const siguiente = sesiones[(i + 1) % sesiones.length] ?? primera;
  return {
    routineId: siguiente.id,
    nombre: siguiente.nombre,
    ultima: hecha === undefined ? null : { nombre: hecha.nombre, fecha: ultimo.fecha },
  };
}

const CORTOS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

/** "Lun · Mié · Vie" */
export function textoDias(dias: readonly number[] | undefined): string {
  return [...(dias ?? [])].sort().map((d) => CORTOS[d] ?? '').filter(Boolean).join(' · ');
}

export const DIAS_SEMANA: readonly { dia: number; corto: string; nombre: string }[] = [
  { dia: 1, corto: 'L', nombre: 'Lunes' },
  { dia: 2, corto: 'M', nombre: 'Martes' },
  { dia: 3, corto: 'M', nombre: 'Miércoles' },
  { dia: 4, corto: 'J', nombre: 'Jueves' },
  { dia: 5, corto: 'V', nombre: 'Viernes' },
  { dia: 6, corto: 'S', nombre: 'Sábado' },
  { dia: 0, corto: 'D', nombre: 'Domingo' },
];
